import type { FieldPath, FieldValues } from 'react-hook-form';
import type { PatternFormField, PatternFormFieldItem, PatternFormFieldItemConstant } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientError } from '@ricoh/ria.client.core/errors';
import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { isNotNull, isType } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type { RoutingFile, RoutingFileCsv, RoutingFileJson, RoutingFileTiff, RoutingFileTxt, RoutingFileXml } from '~/common/model/api/batch-type/process-flow';

import batchTypeLogic from '~/common/logic/feature/settings/batch-type-library/batch-type';
import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

import type {
  RoutingSettingsFileCsvFormField,
  RoutingSettingsFileFormField,
  RoutingSettingsFileJsonFormField,
  RoutingSettingsFileTiffFormField,
  RoutingSettingsFileTxtFormField,
  RoutingSettingsFileXmlFormField,
  TaskRoutingValidationRules,
} from './_routing-cloud.type';

import { isIntegrationDrive, isIntegrationFolder } from './_common';

const logger = createLogger('logic/feature/settings/batch-type-library');

// * Transformation to text for routing
const IS_TRANSFORMATION_TEXT = true;

/**
 * Use TaskRoutingValidationRules.
 *
 * @template TFieldValues FieldValues object.
 * @template TFieldName Field name.
 * @returns {TaskRoutingValidationRules<TFieldValues, TFieldName>} TaskRoutingValidationRules.
 */
export function useTaskRoutingValidationRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(): TaskRoutingValidationRules<TFieldValues, TFieldName> {
  const { t } = useTranslation();

  const requiredFolder = useCallback<Func<[fieldName: string, folder: unknown], true | string>>(
    (fieldName, folder) => {
      if (isIntegrationDrive(folder) || isIntegrationFolder(folder)) {
        return true;
      }

      return t(`validation:required`, { fieldName });
    },
    [t],
  );

  const isValidFolderPath = useCallback<Func<[fieldName: string, value: PatternFormField], true | string>>(
    (fieldName, value) => {
      const invalidItems = value.filter((item) => {
        if (!isType<PatternFormFieldItemConstant>(item, 'const') || typeof item.value !== 'string') {
          return false;
        }

        // Not has invalid characters
        if (/["*:<>?|\\]/.test(item.value)) {
          return true;
        }

        // Not has double slash
        return !!/\/\//.test(item.value);
      });

      return invalidItems.length ? t(`validation:invalid`, { fieldName }) : true;
    },
    [t],
  );

  const isDuplicatedFiles = useCallback<Func<[fieldName: string, value: PatternFormField], true | string>>(
    (fieldName, value) => {
      const itemsSet = new Set();

      const hasDuplicates = value.some((item) => {
        if (isType<PatternFormFieldItemConstant>(item, 'const')) {
          return false;
        }

        const key = `${item.tabKey}-${item.value}`;
        if (itemsSet.has(key)) {
          return true; // Duplicate found
        }

        itemsSet.add(key);
        return false;
      });

      return hasDuplicates ? t(`validation:duplicated`, { fieldName }) : true;
    },
    [t],
  );

  return useMemo<TaskRoutingValidationRules<TFieldValues, TFieldName>>(
    () => ({
      folder: (fieldName) => ({
        required: (_value, { folder }) => requiredFolder(fieldName, folder),
      }),
      folderPath: (fieldName) => ({
        valid: (value) => isValidFolderPath(fieldName, value),
      }),
      duplicatedFiles: (fieldName) => ({
        valid: (value) => isDuplicatedFiles(fieldName, value),
      }),
    }),
    [isDuplicatedFiles, isValidFolderPath, requiredFolder],
  );
}

/**
 * Create Pattern from FieldValue PatternFormField.
 *
 * @param {PatternFormField} formField FieldValue PatternFormField.
 *
 * @returns {Pattern[]} FieldValue Pattern.
 */
function createFieldValuePattern(formField: PatternFormField): Pattern[] {
  const scope: PatternAccessScope = 'document';

  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (isType<PatternFormFieldItemConstant>(item, 'const')) {
      pattern = patternLogic.createPattern.fromConstant('text', item.value);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create FieldValue PatternFormField from Pattern.
 *
 * @param {Pattern[]} fieldValuePattern FieldValue Pattern.
 *
 * @returns {PatternFormField} FieldValue PatternFormField.
 */
export function createFieldValuePatternFormField(fieldValuePattern: Pattern[]): PatternFormField {
  return fieldValuePattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Create RoutingFile FormField.
 *
 * @param {RoutingFile} file RoutingFile.
 *
 * @returns {RoutingSettingsFileFormField} RoutingFile FormField.
 */
export function createRoutingFileFormField(file: RoutingFile): RoutingSettingsFileFormField {
  if (isType<RoutingFileTiff>(file, 'tiff')) {
    const { isSeparatePages, namingRule, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRuleFormFieldItem(namingRule),
      isMultiplePages: !isSeparatePages,
    };
  }

  if (isType<RoutingFileCsv>(file, 'csv')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRuleFormFieldItem(namingRule),
      template: {
        ...template,
        fields: template.fields.map((field) => ({
          ...field,
          value: createFieldValuePatternFormField(field.value),
        })),
      },
    };
  }

  if (isType<RoutingFileJson>(file, 'json')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRuleFormFieldItem(namingRule),
      template: {
        ...template,
        fields: template.fields.map((field) => ({
          ...field,
          value: createFieldValuePatternFormField(field.value),
        })),
      },
    };
  }

  if (isType<RoutingFileXml>(file, 'xml')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRuleFormFieldItem(namingRule),
      template: {
        ...template,
        fields: template.fields.map((field) => ({
          ...field,
          value: createFieldValuePatternFormField(field.value),
        })),
      },
    };
  }

  if (isType<RoutingFileTxt>(file, 'txt')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRuleFormFieldItem(namingRule),
      template: {
        ...template,
        values: template.values.map(({ value }) => {
          const [pattern] = createFieldValuePatternFormField(value);
          if (!pattern) {
            throw new ClientError({
              module: 'logic',
              message: 'Invalid pattern routing field',
              cause: { pattern },
            });
          }

          return pattern;
        }),
      },
    };
  }

  const { namingRule, ...fileSettings } = file;
  return {
    ...fileSettings,
    namingRule: batchTypeLogic.namingRule.createNamingRuleFormFieldItem(namingRule),
  };
}

/**
 * Create RoutingFile from FormField.
 *
 * @param {RoutingSettingsFileFormField} file RoutingFile FormField.
 *
 * @returns {RoutingFile} RoutingFile.
 */
export function createRoutingFile(file: RoutingSettingsFileFormField): RoutingFile {
  if (isType<RoutingSettingsFileTiffFormField>(file, 'tiff')) {
    const { isMultiplePages, namingRule, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRulePattern(namingRule, 'document'),
      isSeparatePages: !isMultiplePages,
    };
  }

  if (isType<RoutingSettingsFileCsvFormField>(file, 'csv')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRulePattern(namingRule, 'document'),
      template: {
        ...template,
        fields: template.fields.map(({ value, ...field }) => ({
          ...field,
          value: createFieldValuePattern(value),
        })),
      },
    };
  }

  if (isType<RoutingSettingsFileJsonFormField>(file, 'json')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRulePattern(namingRule, 'document'),
      template: {
        ...template,
        fields: template.fields.map(({ value, ...field }) => ({
          ...field,
          value: createFieldValuePattern(value),
        })),
      },
    };
  }

  if (isType<RoutingSettingsFileXmlFormField>(file, 'xml')) {
    const { namingRule, template, ...fileSettings } = file;

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRulePattern(namingRule, 'document'),
      template: {
        ...template,
        fields: template.fields.map(({ value, ...field }) => ({
          ...field,
          value: createFieldValuePattern(value),
        })),
      },
    };
  }

  if (isType<RoutingSettingsFileTxtFormField>(file, 'txt')) {
    const { namingRule, template, ...fileSettings } = file;
    const patterns = batchTypeLogic.namingRule.createNamingRulePattern(template.values, 'document');

    return {
      ...fileSettings,
      namingRule: batchTypeLogic.namingRule.createNamingRulePattern(namingRule, 'document'),
      template: {
        ...template,
        values: patterns.map((pattern, index) => ({
          id: newKey(),
          displayName: `field${index}`,
          value: [pattern],
        })),
      },
    };
  }

  const { namingRule, ...fileSettings } = file;
  return {
    ...fileSettings,
    namingRule: batchTypeLogic.namingRule.createNamingRulePattern(namingRule, 'document'),
  };
}
